import { useState } from "react";
import type { GameInfo, TimerKind } from "./lib/catalogue";
import { changeVariant } from "./lib/settings-model";
import { Segmented, Stepper, SwitchRow } from "./ui";

const seconds = (n: number) =>
  n < 60
    ? `${n}s`
    : n % 60 === 0
      ? `${n / 60} min`
      : `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;

/** Game settings for the Create sheet and the lobby Settings sheet. Keys and values
 * are exactly what the server validates (see server/engine/metadata.ts). */
export function Settings({
  game,
  settings,
  onChange,
}: {
  game: GameInfo;
  settings: Record<string, any>;
  onChange: (s: Record<string, any>) => void;
}) {
  const [custom, setCustom] = useState(settings.packId === "custom");
  const [text, setText] = useState(
    () =>
      settings.customWords?.join("\n") ||
      settings.customLocations?.map((l: any) => l.name).join("\n") ||
      settings.customPairs?.map((p: any) => `${p.a} | ${p.b}`).join("\n") ||
      settings.customScales
        ?.map((p: any) => `${p.left} | ${p.right}`)
        .join("\n") ||
      "",
  );
  const set = (key: string, value: unknown) => {
    if (key === "variant" && (value === "blank" || value === "related")) {
      setCustom(false);
      setText("");
      onChange(changeVariant(settings, value));
    } else onChange({ ...settings, [key]: value });
  };
  const toggle = (key: string, label: string, hint?: string) => (
    <SwitchRow
      key={key}
      label={label}
      hint={hint}
      checked={Boolean(settings[key])}
      onChange={(v) => set(key, v)}
    />
  );
  function pack(value: string, enabled = custom) {
    setText(value);
    const s: Record<string, any> = {
      ...settings,
      packId: enabled ? "custom" : game.defaults.packId,
    };
    for (const k of [
      "customWords",
      "customPairs",
      "customLocations",
      "customScales",
    ])
      delete s[k];
    if (enabled) {
      const lines = value
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean);
      if (game.id === "impostor") {
        if (settings.variant === "related")
          s.customPairs = lines.map((l) => {
            const [a, b] = l.split("|").map((x) => x.trim());
            return { a, b };
          });
        else s.customWords = lines;
      }
      if (game.id === "alibi")
        s.customLocations = lines.map((name) => ({ name }));
      if (game.id === "spectrum")
        s.customScales = lines.map((l) => {
          const [left, right] = l.split("|").map((x) => x.trim());
          return { left, right };
        });
    }
    onChange(s);
  }
  const paired =
    game.id === "spectrum" ||
    (game.id === "impostor" && settings.variant === "related");
  return (
    <div className="settings-fields">
      {game.id === "impostor" ? (
        <Segmented
          label="Impostor card"
          value={settings.variant ?? "blank"}
          onChange={(v) => set("variant", v)}
          options={[
            {
              value: "blank",
              label: "Blank",
              hint: "The impostor gets no word and has to bluff from the clues.",
            },
            {
              value: "related",
              label: "Related word",
              hint: "The impostor gets a close but different word, like Tea for Coffee.",
            },
          ]}
        />
      ) : null}
      {game.id === "mafia" ? (
        <div className="settings-group">
          <Stepper
            label="Mafia"
            hint="Auto picks 1 below 8 players, 2 below 12, then 3."
            value={settings.mafiaCount ?? "auto"}
            min={1}
            max={3}
            special={{ value: "auto", label: "Auto" }}
            onChange={(v) => set("mafiaCount", v)}
          />
          {toggle(
            "detective",
            "Include a detective",
            "Learns one player’s side each night.",
          )}
          {toggle(
            "doctor",
            "Include a doctor",
            "Protects one player from the mafia each night.",
          )}
        </div>
      ) : null}
      {game.id === "liar-dice" ? (
        <div className="settings-group">
          <Stepper
            label="Starting dice"
            hint="Each player’s hand at the start."
            value={settings.startingDice ?? 5}
            min={1}
            max={5}
            onChange={(v) => set("startingDice", Number(v))}
          />
          {toggle(
            "onesWild",
            "Ones are wild",
            "Ones count towards every face when a bid is checked.",
          )}
        </div>
      ) : null}
      {game.id === "alibi" ? (
        <Segmented
          label="Conversation"
          value={settings.conversationMode ?? "spoken"}
          onChange={(v) => set("conversationMode", v)}
          options={[
            {
              value: "spoken",
              label: "Spoken",
              hint: "Ask and answer out loud; the app tracks whose turn it is.",
            },
            {
              value: "text",
              label: "Text",
              hint: "Type questions and answers; everyone reads them on screen.",
            },
          ]}
        />
      ) : null}
      {game.id === "spectrum" ? (
        <Segmented
          label="Target range"
          value={settings.targetMode ?? "full-scale"}
          onChange={(v) => set("targetMode", v)}
          options={[
            {
              value: "full-scale",
              label: "Full scale",
              hint: "The target can land anywhere from 0 to 100.",
            },
            {
              value: "inner-scale",
              label: "Inner scale",
              hint: "The target stays between 10 and 90. A little easier.",
            },
          ]}
        />
      ) : null}
      {["impostor", "alibi", "spectrum"].includes(game.id) ? (
        <div className="settings-group">
          <SwitchRow
            label="Use a custom pack"
            hint={
              game.id === "alibi"
                ? "Bring your own list of places."
                : game.id === "spectrum"
                  ? "Bring your own opposite pairs."
                  : "Bring your own words."
            }
            checked={custom}
            onChange={(checked) => {
              setCustom(checked);
              pack(text, checked);
            }}
          />
          {custom ? (
            <label className="ui-field pack-field">
              <span className="ui-label">
                {game.id === "spectrum"
                  ? "Opposite pairs, one per line"
                  : paired
                    ? "Related pairs, one per line"
                    : "Entries, one per line"}
              </span>
              <textarea
                rows={5}
                value={text}
                onChange={(e) => pack(e.target.value)}
                placeholder={
                  game.id === "spectrum"
                    ? "Tiny surprise | Life-changing surprise"
                    : paired
                      ? "Coffee | Tea"
                      : "Rooftop greenhouse"
                }
              />
              <span className="ui-hint">
                8–100 unique entries, up to 48 characters each.
                {paired ? " Separate each pair with |." : ""}
              </span>
            </label>
          ) : null}
        </div>
      ) : null}
      <details className="more-options">
        <summary>
          <span>More options</span>
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="m6 9 6 6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </summary>
        <div className="settings-fields">
          <Segmented
            label="Pace"
            value={settings.timerMode ?? "relaxed"}
            onChange={(v) => set("timerMode", v)}
            options={[
              {
                value: "relaxed",
                label: "Relaxed",
                hint: "No countdowns. Take as long as the table needs.",
              },
              {
                value: "guided",
                label: "Guided",
                hint: "Soft time limits. An unfinished turn pauses the game; it never invents a move.",
              },
            ]}
          />
          {settings.timerMode === "guided" ? (
            <div className="settings-group timing-fields">
              {(
                [
                  ["clueSeconds", "Clue seconds", 30, 180, 45],
                  ["decisionSeconds", "Decision seconds", 30, 180, 60],
                  ["turnSeconds", "Turn seconds", 30, 180, 60],
                  ["discussionSeconds", "Discussion seconds", 60, 600, 120],
                ] as const
              )
                .filter(([key]) =>
                  game.timers.includes(key.replace("Seconds", "") as TimerKind),
                )
                .map(([key, label, min, max, fallback]) => (
                <Stepper
                  key={key}
                  label={label}
                  value={settings[key] ?? fallback}
                  min={min}
                  max={max}
                  step={15}
                  format={seconds}
                  onChange={(v) => set(key, Number(v))}
                />
              ))}
            </div>
          ) : null}
          {game.id === "impostor" ? (
            <>
              <Segmented
                label="Clue passes"
                value={Number(settings.cluePasses ?? 1)}
                onChange={(v) => set("cluePasses", v)}
                options={[
                  { value: 1, label: "1", hint: "Everyone gives one clue." },
                  { value: 2, label: "2", hint: "Two rounds of clues." },
                  { value: 3, label: "3", hint: "Three rounds of clues." },
                ]}
              />
              <div className="settings-group">
                {toggle(
                  "finalGuess",
                  "Impostor gets a final guess",
                  "If caught, they can still win by naming the word.",
                )}
              </div>
            </>
          ) : null}
          {game.id === "mafia" ? (
            <div className="settings-group">
              {toggle(
                "doctorSelfSave",
                "Doctor can protect themself",
                "Otherwise the doctor must pick someone else.",
              )}
              {toggle(
                "doctorRepeatSave",
                "Allow repeat protection",
                "The same person can be protected two nights running.",
              )}
              {toggle(
                "revealEliminatedRoles",
                "Reveal eliminated roles",
                "Show a player’s role when they are out.",
              )}
            </div>
          ) : null}
          {game.id === "alibi" ? (
            <>
              <div className="settings-group">
                <Stepper
                  label="Possible locations"
                  hint="A public shortlist; the chosen place stays private. Smaller custom packs cap the shortlist."
                  value={settings.candidateCount ?? 8}
                  min={6}
                  max={12}
                  onChange={(v) => set("candidateCount", Number(v))}
                />
              </div>
              <Segmented
                label="Question passes"
                value={Number(settings.questionRounds ?? 1)}
                onChange={(v) => set("questionRounds", v)}
                options={[
                  { value: 1, label: "1", hint: "Everyone asks one question." },
                  { value: 2, label: "2", hint: "Everyone asks two questions." },
                ]}
              />
              <div className="settings-group">
                {toggle(
                  "spyEarlyGuess",
                  "Spy can guess early",
                  "The spy may name the place before the vote.",
                )}
              </div>
            </>
          ) : null}
          {game.id === "spectrum" ? (
            <div className="settings-group">
              <Stepper
                label="Rounds"
                hint="Everyone takes one turn as the guide by default."
                value={settings.rounds ?? "everyone"}
                min={2}
                max={12}
                special={{ value: "everyone", label: "Everyone" }}
                onChange={(v) => set("rounds", v)}
              />
            </div>
          ) : null}
        </div>
      </details>
    </div>
  );
}
